/**
 * micHandoff — keeps the microphone from the camera-setup mic check until RAN.
 *
 * Firefox only remembers a microphone permission while the microphone is on
 * (plus a few seconds), unless the parent ticks "Remember". Stopping the mic
 * after the setup check and asking again at RAN therefore showed a second
 * permission prompt, and the prompt takes the page out of full screen.
 * Chrome and Edge remember the permission for the site, so they never asked.
 *
 * Instead the setup page hands its stream over here, muted (track.enabled =
 * false, so nothing is captured), and RAN takes it and unmutes it. Leaving the
 * setup → calibration/Word-Icon → RAN part of the flow releases it (App.tsx),
 * and a safety timer releases it if it is never picked up.
 */
import { ROUTES } from '../../constants/routes';

/** Routes between the mic check and RAN, where a held mic may stay held. */
export const MIC_HOLD_ROUTES: ReadonlySet<string> = new Set([ROUTES.ALLOW_CAMERA, ROUTES.CALIBRATION, ROUTES.RAN_TEST]);

const SAFETY_RELEASE_MS = 30 * 60 * 1000;

let held: MediaStream | null = null;
let safetyTimer: ReturnType<typeof setTimeout> | null = null;

function clearSafetyTimer(): void {
  if (safetyTimer) clearTimeout(safetyTimer);
  safetyTimer = null;
}

/** Keep the stream (muted) for RAN. Replaces and stops any stream held before. */
export function holdMicForLater(stream: MediaStream): void {
  if (held && held !== stream) releaseHeldMic();
  stream.getAudioTracks().forEach(t => { t.enabled = false; });
  held = stream;
  clearSafetyTimer();
  safetyTimer = setTimeout(releaseHeldMic, SAFETY_RELEASE_MS);
}

/** Take the held stream, unmuted; null when none is held or it has ended. The caller then owns it. */
export function takeHeldMic(): MediaStream | null {
  const stream = held;
  held = null;
  clearSafetyTimer();
  if (!stream) return null;
  const live = stream.getAudioTracks().filter(t => t.readyState === 'live');
  if (live.length === 0) {
    stream.getTracks().forEach(t => t.stop());
    return null;
  }
  live.forEach(t => { t.enabled = true; });
  return stream;
}

/** Stop and forget the held stream, if any. */
export function releaseHeldMic(): void {
  clearSafetyTimer();
  held?.getTracks().forEach(t => t.stop());
  held = null;
}

export function isMicHeld(): boolean {
  return held !== null;
}
